// /admin/support — the read side of a table that only ever accepted writes.
//
// No reply composer, on purpose. Replies go through the operator's own mail
// client via `mailto:`; an outbound path here would mean owning deliverability,
// threading and a From address for one person's inbox. Also cut: attachments,
// assignment, and a per-ticket route.
//
// Subject is a `Button variant="ghost"` rather than a `ListRow` because a
// ticket has no URL — `ListRow` insists on a `to`/`href`, and inventing a route
// so a list component is happy is the wrong way round.

import { useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  PageHeader,
  Skeleton,
  SkeletonStack,
  Table,
} from "../../ui";
import type { TableColumn } from "../../ui";
import { relativeTime } from "../content/relativeTime";
import { useResolveTicket, useTickets } from "./api";
import type { Ticket } from "./api";
import "./admin.css";

export function SupportInboxScreen() {
  const tickets = useTickets();
  const resolve = useResolveTicket();
  const [openId, setOpenId] = useState<string | null>(null);

  const header = (
    <PageHeader
      eyebrow="Operator"
      title="Support inbox"
      description="Open tickets across every tenant, newest first."
    />
  );

  if (tickets.isPending) {
    return (
      <div className="maisight-admin">
        {header}
        <SkeletonStack label="Loading tickets">
          <Skeleton width={280} height={28} />
          <Skeleton width={620} />
        </SkeletonStack>
      </div>
    );
  }

  if (tickets.isError || !tickets.data) {
    return (
      <div className="maisight-admin">
        {header}
        <ErrorState headline="Could not load the support inbox" />
      </div>
    );
  }

  const open = tickets.data.find((t) => t.ticket_id === openId) ?? null;

  return (
    <div className="maisight-admin">
      {header}
      {open ? (
        <Card header={open.subject}>
          <p className="type-bodyS maisight-admin-ticket-meta">
            {open.org_slug} · {open.email} · {relativeTime(open.created_at)}
          </p>
          <p className="type-body maisight-admin-ticket-body">{open.body}</p>
          <div className="maisight-admin-ticket-actions">
            <Button
              href={`mailto:${open.email}?subject=${encodeURIComponent(`Re: ${open.subject}`)}`}
              leading={<Icon name="mail" size={16} />}
            >
              Reply by email
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                resolve.mutate(open.ticket_id, { onSuccess: () => setOpenId(null) })
              }
            >
              {resolve.isPending ? "Resolving…" : "Mark resolved"}
            </Button>
            <Button variant="ghost" onClick={() => setOpenId(null)}>
              Close
            </Button>
          </div>
        </Card>
      ) : null}

      {tickets.data.length === 0 ? (
        <EmptyState
          icon="life-buoy"
          headline="No open tickets"
          body="Anything a customer submits from their Support screen lands here."
        />
      ) : (
        <Card tone="flat" header="Open tickets">
          <Table<Ticket>
            label="Support tickets"
            columns={columns(setOpenId)}
            rows={tickets.data}
            rowKey={(row) => row.ticket_id}
            total={tickets.data.length}
            unit="tickets"
          />
        </Card>
      )}
    </div>
  );
}

function columns(onOpen: (id: string) => void): TableColumn<Ticket>[] {
  return [
    {
      key: "subject",
      header: "Subject",
      cell: (row) => (
        <Button variant="ghost" size="sm" onClick={() => onOpen(row.ticket_id)}>
          {row.subject}
        </Button>
      ),
    },
    { key: "org", header: "Org", cell: (row) => row.org_slug },
    { key: "email", header: "From", cell: (row) => row.email },
    { key: "when", header: "Received", cell: (row) => relativeTime(row.created_at) },
  ];
}
